import { useEffect } from "react";
import type { TLCommentThreadId } from "@tldraw/tlschema";
import type { WorkspaceModel } from "./useWorkspace";
import type { InboxItem } from "../shared/updates";
import {
  notificationWasPresented,
  type PresentedReview,
} from "../shared/review-presentation";
import { changeSignature } from "../shared/change-facts";
import { api } from "./api";
import { pendingReviewReceipt, forgetReviewReceipt } from "./review-receipts";
// Only mounted, visible discussion content qualifies. Scrolling into a message
// starts its exposure; hover previews and background tabs never count.
export function useDiscussionSeen(model: WorkspaceModel) {
  const { room, threadId, editor, connected } = model;
  useEffect(() => {
    if (!threadId || !connected) return;
    let live = true,
      busy = false,
      nextInbox = 0;
    let revealed = "";
    const since = new Map<string, number>(),
      sent = new Map<string, number>();
    const presented = (): PresentedReview => {
      const shown: PresentedReview = { comments: [], actions: [] };
      const active = new Set<string>();
      const popover = [
        ...document.querySelectorAll<HTMLElement>("[data-review-thread]"),
      ].find((e) => e.dataset.reviewThread === threadId);
      if (!popover || document.hidden || !document.hasFocus()) {
        since.clear();
        return shown;
      }
      const intent = pendingReviewReceipt(room.id, threadId);
      const intentKey = intent ? `${intent.id}:${intent.updated_at}` : "";
      if(intent?.source_comment_id && revealed!==intentKey) {
        const target=[...popover.querySelectorAll<HTMLElement>("[data-review-comment]")].find(el=>el.dataset.reviewComment===intent.source_comment_id);
        if(target){target.scrollIntoView({block:"nearest"});revealed=intentKey;}
      }
      const bounds = popover.getBoundingClientRect();
      for (const el of popover.querySelectorAll<HTMLElement>(
        "[data-review-comment],[data-review-action]",
      )) {
        const rect = el.getBoundingClientRect(),
          clip = el.closest(".pin-messages")?.getBoundingClientRect() ?? bounds;
        const height =
          Math.min(rect.bottom, clip.bottom, bounds.bottom, innerHeight) -
          Math.max(rect.top, clip.top, bounds.top, 0);
        const width =
          Math.min(rect.right, bounds.right, innerWidth) -
          Math.max(rect.left, bounds.left, 0);
        if (
          height < Math.min(rect.height * 0.6, 80) ||
          width < Math.min(rect.width * 0.6, 100) ||
          rect.width === 0 ||
          rect.height === 0
        )
          continue;
        const id = el.dataset.reviewComment ?? el.dataset.reviewAction!;
        const signature =
          el.dataset.reviewSignature ?? el.dataset.reviewUpdated!;
        const key = `${id}:${signature}`;
        active.add(key);
        if (!since.has(key)) since.set(key, Date.now());
        if (Date.now() - since.get(key)! < 1000) continue;
        if (el.dataset.reviewComment) shown.comments.push({ id, signature, occurrences:JSON.parse(el.dataset.reviewOccurrences??"[]") });
        else shown.actions.push({ id, updatedAt: Number(signature) });
      }
      for (const key of since.keys()) if (!active.has(key)) since.delete(key);
      return shown;
    };
    const tick = async () => {
      if (busy || !live) return;
      const shown = presented();
      if (!shown.comments.length && !shown.actions.length) return;
      busy = true;
      try {
        const records = shown.comments
          .filter(
            (r) =>
              sent.get(`${r.id}:${r.signature}`) !==
              since.get(`${r.id}:${r.signature}`),
          )
          .slice(0, 49);
        const thread = editor.store.get(threadId as TLCommentThreadId);
        if (records.length && thread?.typeName === "comment-thread")
          records.push({ id: thread.id, signature: changeSignature(thread) });
        if (records.length) {
          await api(`/api/rooms/${room.id}/changes`, {
            action: "interact",
            records,
          });
          if (!live) return;
          for (const r of records)
            sent.set(
              `${r.id}:${r.signature}`,
              since.get(`${r.id}:${r.signature}`) ?? Date.now(),
            );
          window.dispatchEvent(new Event("planroom:changes-seen"));
        }
        if (room.platform === "cloudflare" && Date.now() >= nextInbox) {
          nextInbox = Date.now() + 5000;
          const data = await api<{ items: InboxItem[] }>(
            `/api/updates?project=${encodeURIComponent(room.id)}&thread=${encodeURIComponent(threadId)}&unread=1`,
          );
          if (!live) return;
          const current = presented();
          for (const item of data.items) {
            if (
              !notificationWasPresented(
                item,
                current,
                pendingReviewReceipt(room.id, threadId),
              )
            )
              continue;
            await api("/api/updates/read", {
              id: item.id,
              board: room.id,
              version: item.updated_at,
            });
            forgetReviewReceipt(item.id);
            if (live) window.dispatchEvent(new Event("planroom:updates-read"));
          }
        }
      } catch {
        nextInbox = Date.now() + 2000;
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(() => void tick(), 500);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [room.id, room.platform, model.user.id, threadId, editor, connected]);
}
